import React from 'react';
import {
  AdjustPanel,
  AdjustRow,
  AdjustLabel,
  AdjustSlider,
  AdjustValue,
  AdjustActionRow,
  HiddenSvg,
  CropActionButton,
} from '../imageCanvasStyles';

const SHARPEN_FILTER_ID = 'fovea-adjust-sharpen';

const SLIDERS = [
  { key: 'brightness', label: 'Brightness', min: -100, max: 100 },
  { key: 'contrast', label: 'Contrast', min: -100, max: 100 },
  { key: 'saturation', label: 'Saturation', min: -100, max: 100 },
  { key: 'sharpness', label: 'Sharpness', min: 0, max: 100 },
];

const DEFAULT_ADJUSTMENTS = { brightness: 0, contrast: 0, saturation: 0, sharpness: 0 };

const buildFilterString = ({ brightness, contrast, saturation, sharpness }) => {
  const parts = [
    `brightness(${1 + brightness / 100})`,
    `contrast(${1 + contrast / 100})`,
    `saturate(${1 + saturation / 100})`,
  ];
  if (sharpness > 0) parts.push(`url(#${SHARPEN_FILTER_ID})`);
  return parts.join(' ');
};

const sharpenKernel = sharpness => {
  const k = sharpness / 100;
  return `0 ${-k} 0 ${-k} ${1 + 4 * k} ${-k} 0 ${-k} 0`;
};

const AdjustOverlay = ({ theme, selectedTheme, imgRef, adjustments, onAdjustmentsChange, onApply, onReset }) => {
  const shared = { theme, $selectedTheme: selectedTheme };

  const handleChange = key => e => {
    onAdjustmentsChange({ ...adjustments, [key]: Number(e.target.value) });
  };

  const handleApply = () => {
    const img = imgRef?.current;
    if (!img) return;

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.filter = buildFilterString(adjustments);
    ctx.drawImage(img, 0, 0);
    onApply(canvas.toDataURL('image/png'));
  };

  return (
    <>
      <HiddenSvg aria-hidden='true'>
        <filter id={SHARPEN_FILTER_ID} x='0' y='0' width='100%' height='100%'>
          <feConvolveMatrix kernelMatrix={sharpenKernel(adjustments.sharpness)} preserveAlpha='true' />
        </filter>
      </HiddenSvg>
      <AdjustPanel {...shared}>
        {SLIDERS.map(({ key, label, min, max }) => (
          <AdjustRow key={key}>
            <AdjustLabel {...shared} htmlFor={`adjust-${key}`}>{label}</AdjustLabel>
            <AdjustSlider
              {...shared}
              id={`adjust-${key}`}
              min={min}
              max={max}
              value={adjustments[key]}
              onChange={handleChange(key)}
            />
            <AdjustValue {...shared}>{adjustments[key]}</AdjustValue>
          </AdjustRow>
        ))}
        <AdjustActionRow>
          <CropActionButton onClick={onReset}>Reset Adjustments</CropActionButton>
          <CropActionButton $primary onClick={handleApply}>Apply</CropActionButton>
        </AdjustActionRow>
      </AdjustPanel>
    </>
  );
};

export { AdjustOverlay, DEFAULT_ADJUSTMENTS, buildFilterString };
